import { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import { ISankeyData, SankeyChart } from 'app/components/charts/SankeyChart';
import colors from 'assets/scss/colors.module.scss';
import { AkumaAktionenVM } from 'app/stores/ui/agt.akumaaktionen.ui.store';
import { EChartsOption } from 'echarts';
import _ from 'lodash';
import { Button } from 'app/components/common/Button';
import { Modal } from 'app/components/Modal';
import { Icon } from 'app/components/common/Icon';

interface IAkumaAktionenSankeyChartType {
	showLegend?: boolean;
}

interface IAkumaAktionenSankeyData extends ISankeyData {
	paths?: string[];
}

interface IAktionenData {
	aktionsbearbeitung: number;
	topAktionen: number;
	aktionen: number;
	topBearbeitet: number;
	topNichtBearbeitet: number;
	aktionenBearbeitet: number;
	aktionenNichtBearbeitet: number;
	topBearbeitetSach: number;
	topBearbeitetLeben: number;
	topBearbeitetKranken: number;
	topBearbeitetUebergreifend: number;
	topNichtBearbeitetSach: number;
	topNichtBearbeitetLeben: number;
	topNichtBearbeitetKranken: number;
	topNichtBearbeitetUebergreifend: number;
	aktionenBearbeitetSach: number;
	aktionenBearbeitetLeben: number;
	aktionenBearbeitetKranken: number;
	aktionenBearbeitetUebergreifend: number;
	aktionenNichtBearbeitetSach: number;
	aktionenNichtBearbeitetLeben: number;
	aktionenNichtBearbeitetKranken: number;
	aktionenNichtBearbeitetUebergreifend: number;
}

const aktionenSankeySchema: IAkumaAktionenSankeyData[] = [
	// Level 1: Root to Top Aktionen and Aktionen
	{ source: 'Aktionstreffer', target: 'Top Aktionen', sourceColor: colors.grey, targetColor: colors.green, paths: ['topAktionen'] },
	{ source: 'Aktionstreffer', target: 'Aktionen', sourceColor: colors.grey, targetColor: colors.blue, paths: ['aktionen'] },

	// Level 2: Bearbeitet vs Nicht bearbeitet for Top Aktionen
	{ source: 'Top Aktionen', target: 'Bearbeitet__top', sourceColor: colors.green, targetColor: colors.darkGreen, paths: ['topBearbeitet'] },
	{ source: 'Top Aktionen', target: 'Nicht bearbeitet__top', sourceColor: colors.green, targetColor: colors.red, paths: ['topNichtBearbeitet'] },

	// Level 2: Bearbeitet vs Nicht bearbeitet for Aktionen
	{ source: 'Aktionen', target: 'Bearbeitet__akt', sourceColor: colors.blue, targetColor: colors.richBlue, paths: ['aktionenBearbeitet'] },
	{ source: 'Aktionen', target: 'Nicht bearbeitet__akt', sourceColor: colors.blue, targetColor: colors.richRed, paths: ['aktionenNichtBearbeitet'] },

	// Level 3: LOB breakdown for Top Bearbeitet
	{ source: 'Bearbeitet__top', target: 'Leben__top_b', targetColor: colors.purple, paths: ['topBearbeitetLeben'] },
	{ source: 'Bearbeitet__top', target: 'Sach__top_b', targetColor: colors.yellow, paths: ['topBearbeitetSach'] },
	{ source: 'Bearbeitet__top', target: 'Kranken__top_b', targetColor: colors.orange, paths: ['topBearbeitetKranken'] },
	{ source: 'Bearbeitet__top', target: 'Übergreifend__top_b', targetColor: colors.brown, paths: ['topBearbeitetUebergreifend'] },

	// Level 3: LOB breakdown for Top Nicht bearbeitet
	{ source: 'Nicht bearbeitet__top', target: 'Leben__top_nb', targetColor: colors.richTurquoise, paths: ['topNichtBearbeitetLeben'] },
	{ source: 'Nicht bearbeitet__top', target: 'Sach__top_nb', targetColor: colors.darkYellow, paths: ['topNichtBearbeitetSach'] },
	{ source: 'Nicht bearbeitet__top', target: 'Kranken__top_nb', targetColor: colors.greyLight, paths: ['topNichtBearbeitetKranken'] },
	{ source: 'Nicht bearbeitet__top', target: 'Übergreifend__top_nb', targetColor: colors.richGreen, paths: ['topNichtBearbeitetUebergreifend'] },

	// Level 3: LOB breakdown for Aktionen Bearbeitet
	{ source: 'Bearbeitet__akt', target: 'Leben__akt_b', targetColor: colors.purple, paths: ['aktionenBearbeitetLeben'] },
	{ source: 'Bearbeitet__akt', target: 'Sach__akt_b', targetColor: colors.yellow, paths: ['aktionenBearbeitetSach'] },
	{ source: 'Bearbeitet__akt', target: 'Kranken__akt_b', targetColor: colors.orange, paths: ['aktionenBearbeitetKranken'] },
	{ source: 'Bearbeitet__akt', target: 'Übergreifend__akt_b', targetColor: colors.brown, paths: ['aktionenBearbeitetUebergreifend'] },

	// Level 3: LOB breakdown for Aktionen Nicht bearbeitet
	{ source: 'Nicht bearbeitet__akt', target: 'Leben__akt_nb', targetColor: colors.richTurquoise, paths: ['aktionenNichtBearbeitetLeben'] },
	{ source: 'Nicht bearbeitet__akt', target: 'Sach__akt_nb', targetColor: colors.darkYellow, paths: ['aktionenNichtBearbeitetSach'] },
	{ source: 'Nicht bearbeitet__akt', target: 'Kranken__akt_nb', targetColor: colors.greyLight, paths: ['aktionenNichtBearbeitetKranken'] },
	{ source: 'Nicht bearbeitet__akt', target: 'Übergreifend__akt_nb', targetColor: colors.richGreen, paths: ['aktionenNichtBearbeitetUebergreifend'] },
];

function getChartData(schema: IAkumaAktionenSankeyData[], data: any): ISankeyData[] {
	const ret: ISankeyData[] = [];

	// iterate through sankey schema nodes
	schema.forEach((node) => {
		node.value = 0;

		node.paths?.forEach((path) => {
			node.value += data[path] || 0;
		});

		// Only add nodes with values > 0
		if (node.value > 0) {
			ret.push({ ...node });
		}
	});

	return ret;
}

export const AkumaAktionenSankeyChart = observer((props: IAkumaAktionenSankeyChartType) => {
	const { showLegend = false } = props;
	const { agtAkumaAktionenUiStore, berichteUiiStore, agenturListUiStore, uiStore } = useStore();

	const [chartData, setChartData] = useState<ISankeyData[]>();
	const [itemsTotal, setItemsTotal] = useState<number>(0);
	const [hideUnbearbeitet, setHideUnbearbeitet] = useState<boolean>(false);

	useEffect(() => {
		let items = agtAkumaAktionenUiStore.items;
		if (berichteUiiStore.selectedAgt) {
			const agtId = berichteUiiStore.selectedAgt.agtId;
			items = items.filter((i) => i.agt.agtId === agtId);
			items = _.sortBy(items, 'bm').reverse();
		} else {
			// Apply base agency filters
			items = agenturListUiStore.applyFilterOnAgt(items) as AkumaAktionenVM[];
			// Apply VM-specific filters
			items = agenturListUiStore.currentFilter.applyVmFilter(items) as AkumaAktionenVM[];
		}

		// Calculate data for Sankey chart based on the overview model
		let data: IAktionenData = {
			aktionsbearbeitung: 0,
			topAktionen: 0,
			aktionen: 0,
			topBearbeitet: 0,
			topNichtBearbeitet: 0,
			aktionenBearbeitet: 0,
			aktionenNichtBearbeitet: 0,
			topBearbeitetSach: 0,
			topBearbeitetLeben: 0,
			topBearbeitetKranken: 0,
			topBearbeitetUebergreifend: 0,
			topNichtBearbeitetSach: 0,
			topNichtBearbeitetLeben: 0,
			topNichtBearbeitetKranken: 0,
			topNichtBearbeitetUebergreifend: 0,
			aktionenBearbeitetSach: 0,
			aktionenBearbeitetLeben: 0,
			aktionenBearbeitetKranken: 0,
			aktionenBearbeitetUebergreifend: 0,
			aktionenNichtBearbeitetSach: 0,
			aktionenNichtBearbeitetLeben: 0,
			aktionenNichtBearbeitetKranken: 0,
			aktionenNichtBearbeitetUebergreifend: 0,
		};

		// Treffer mode - sum customer hits across agencies
		items.forEach((item) => {
			if (item.hasOverviewItem && item.overviewItem) {
				const overview = item.overviewItem as any;

				data.aktionsbearbeitung += overview.aktionenCustumerCount || 0;
				data.topAktionen += overview.topCustumerCount || 0;
				data.aktionen += (overview.aktionenCustumerCount || 0) - (overview.topCustumerCount || 0);
				data.topBearbeitet += overview.topAgencyCount || 0;
				data.topNichtBearbeitet += (overview.topCustumerCount || 0) - (overview.topAgencyCount || 0);
				data.aktionenBearbeitet += (overview.aktionenAgencyCount || 0) - (overview.topAgencyCount || 0);
				data.aktionenNichtBearbeitet += (overview.aktionenCustumerCount || 0) - (overview.topCustumerCount || 0) - ((overview.aktionenAgencyCount || 0) - (overview.topAgencyCount || 0));

				data.topBearbeitetSach += overview.topsachAgencyCount || 0;
				data.topBearbeitetLeben += overview.toplebenAgencyCount || 0;
				data.topBearbeitetKranken += overview.topkrankenAgencyCount || 0;
				data.topBearbeitetUebergreifend += overview.topuebergreifendAgencyCount || 0;

				data.topNichtBearbeitetSach += (overview.topsachCustumerCount || 0) - (overview.topsachAgencyCount || 0);
				data.topNichtBearbeitetLeben += (overview.toplebenCustumerCount || 0) - (overview.toplebenAgencyCount || 0);
				data.topNichtBearbeitetKranken += (overview.topkrankenCustumerCount || 0) - (overview.topkrankenAgencyCount || 0);
				data.topNichtBearbeitetUebergreifend += (overview.topuebergreifendCustumerCount || 0) - (overview.topuebergreifendAgencyCount || 0);

				data.aktionenBearbeitetSach += (overview.sachAgencyCount || 0) - (overview.topsachAgencyCount || 0);
				data.aktionenBearbeitetLeben += (overview.lebenAgencyCount || 0) - (overview.toplebenAgencyCount || 0);
				data.aktionenBearbeitetKranken += (overview.krankenAgencyCount || 0) - (overview.topkrankenAgencyCount || 0);
				data.aktionenBearbeitetUebergreifend += (overview.uebergreifendAgencyCount || 0) - (overview.topuebergreifendAgencyCount || 0);

				data.aktionenNichtBearbeitetSach += (overview.sachCustumerCount || 0) - (overview.topsachCustumerCount || 0) - ((overview.sachAgencyCount || 0) - (overview.topsachAgencyCount || 0));
				data.aktionenNichtBearbeitetLeben += (overview.lebenCustumerCount || 0) - (overview.toplebenCustumerCount || 0) - ((overview.lebenAgencyCount || 0) - (overview.toplebenAgencyCount || 0));
				data.aktionenNichtBearbeitetKranken += (overview.krankenCustumerCount || 0) - (overview.topkrankenCustumerCount || 0) - ((overview.krankenAgencyCount || 0) - (overview.topkrankenAgencyCount || 0));
				data.aktionenNichtBearbeitetUebergreifend += (overview.uebergreifendCustumerCount || 0) - (overview.topuebergreifendCustumerCount || 0) - ((overview.uebergreifendAgencyCount || 0) - (overview.topuebergreifendAgencyCount || 0));
			}
		});

		// Filter schema to exclude "Nicht bearbeitet" paths if toggle is active
		let filteredSchema = aktionenSankeySchema;
		let adjustedData = { ...data };

		if (hideUnbearbeitet) {
			filteredSchema = aktionenSankeySchema.filter((node) => {
				// Exclude nodes that contain "Nicht bearbeitet" in source or target
				return !node.source.includes('Nicht bearbeitet') && !node.target.includes('Nicht bearbeitet');
			});

			// Recalculate totals by subtracting unprocessed values
			adjustedData.aktionsbearbeitung = data.topBearbeitet + data.aktionenBearbeitet;
			adjustedData.topAktionen = data.topBearbeitet;
			adjustedData.aktionen = data.aktionenBearbeitet;
		}

		const cdata = getChartData(filteredSchema, adjustedData);
		setChartData(cdata);

		// Set itemsTotal to root node value
		setItemsTotal(adjustedData.aktionsbearbeitung);
	}, [agenturListUiStore.filterHasChanged, agenturListUiStore, agenturListUiStore.items, agtAkumaAktionenUiStore, agtAkumaAktionenUiStore.items, berichteUiiStore.selectedAgt, hideUnbearbeitet]);

	const labelFormat = (params: any) => {
		if (itemsTotal === 0) {
			return ``;
		}
		// Strip out the ID suffix (everything after "__") for clean display
		const displayName = params.name.split('__')[0];
		const perc: string = itemsTotal > 0 ? `{italic|(${Math.round((params.value / itemsTotal) * 100)}%)}` : '';
		return `{bold|${displayName}} ${params.value} ${perc}`;
	};

	const opts: EChartsOption = {
		series: {
			right: 300,
			label: {
				overflow: 'break',
			},
			labelLayout: {
				width: 220,
			},
		},
	};

	const openModal = () => {
		uiStore.showModal('akumaAktionenLegend');
	};
	const closeModal = () => {
		uiStore.hideModal('akumaAktionenLegend');
	};

	// Check if there's any data to display
	const hasData = chartData && chartData.length > 0 && itemsTotal > 0;

	return (
		<>
			{hasData ? (
				<SankeyChart data={chartData} labelFormatter={labelFormat} opts={opts} />
			) : (
				<div className="notification is-info">
					<p>Keine Daten vorhanden für die ausgewählte Agentur und Kalenderwoche.</p>
				</div>
			)}

			<>
				<div className="pad-top-1rem">
					<Button
						className="button is-primary is-small is-inverted"
						onClick={() => {
							setHideUnbearbeitet(!hideUnbearbeitet);
						}}
					>
						{!hideUnbearbeitet ? 'Nicht bearbeitete Kunden ausblenden' : 'Nicht bearbeitete Kunden einblenden'}
					</Button>
					<Button className="button is-primary is-small is-inverted" onClick={openModal}>
						<Icon iconClass="info-circle" />
						<span>Legende</span>
					</Button>
				</div>
				{showLegend && (
					<>
						<Modal modalId={'akumaAktionenLegend'} title={'Erläuterung der Dargestellten Kennzahlen'} onClose={closeModal}>
							<div>
								<h4>Akuma Aktionen Sankey Diagramm</h4>
								<p>Dieses Diagramm zeigt den Fluss der Akuma Aktionsbearbeitung:</p>
								<ul>
									<li>
										<strong>Aktionsbearbeitung:</strong> Alle Aktionen im System
									</li>
									<li>
										<strong>Top Aktionen:</strong> Aktionen, die als Top-Aktionen markiert sind
									</li>
									<li>
										<strong>Aktionen:</strong> Alle anderen Aktionen
									</li>
									<li>
										<strong>Bearbeitet:</strong> Aktionen, die von der Agentur bearbeitet wurden
									</li>
									<li>
										<strong>Nicht bearbeitet:</strong> Aktionen, die noch nicht bearbeitet wurden
									</li>
									<li>
										<strong>Leben/Sach/Kranken/Übergreifend:</strong> Aufteilung nach Versicherungssparten
									</li>
								</ul>
							</div>
						</Modal>
					</>
				)}
			</>
		</>
	);
});
